import type { Endpoint, HttpMethod } from "../types";

export type CodeLanguage = "curl" | "node" | "python" | "go";

export const LANGUAGE_LABELS: Record<CodeLanguage, string> = {
  curl: "cURL",
  node: "Node.js",
  python: "Python",
  go: "Go",
};

interface CodeContext {
  bearerToken?: string;
  body?: string;
  endpoint: Endpoint;
  url: string;
}

const escapeShell = (raw: string) => raw.replace(/'/g, "'\\''");

const indent = (raw: string, spaces: number) => {
  const pad = " ".repeat(spaces);
  return raw
    .split("\n")
    .map(line => (line ? pad + line : line))
    .join("\n");
};

const hasBody = (method: HttpMethod) =>
  method === "POST" || method === "PUT" || method === "PATCH";

const tokenOf = (ctx: CodeContext) => ctx.bearerToken || "<bearer-token>";

const buildCurl = (ctx: CodeContext) => {
  const lines = [
    `curl -X ${ctx.endpoint.method} '${ctx.url}' \\`,
    `  -H 'Authorization: Bearer ${tokenOf(ctx)}' \\`,
    `  -H 'Content-Type: application/json' \\`,
    `  -H 'Accept: application/json'`,
  ];
  if (hasBody(ctx.endpoint.method) && ctx.body) {
    lines[lines.length - 1] += " \\";
    lines.push(`  -d '${escapeShell(ctx.body)}'`);
  }
  return lines.join("\n");
};

const buildNode = (ctx: CodeContext) => {
  const headers = [
    `    "Authorization": "Bearer ${tokenOf(ctx)}",`,
    `    "Content-Type": "application/json",`,
    `    "Accept": "application/json",`,
  ].join("\n");
  const opts = [
    `  method: "${ctx.endpoint.method}",`,
    `  headers: {\n${headers}\n  },`,
  ];
  if (hasBody(ctx.endpoint.method) && ctx.body) {
    opts.push(`  body: JSON.stringify(${ctx.body}),`);
  }
  return [
    `const response = await fetch("${ctx.url}", {`,
    opts.join("\n"),
    `});`,
    ``,
    `if (!response.ok) {`,
    `  throw new Error(\`Request failed: \${response.status}\`);`,
    `}`,
    ``,
    `const data = await response.json();`,
    `console.log(data);`,
  ].join("\n");
};

const buildPython = (ctx: CodeContext) => {
  const lines = [
    `import requests`,
    ``,
    `url = "${ctx.url}"`,
    `headers = {`,
    `    "Authorization": "Bearer ${tokenOf(ctx)}",`,
    `    "Content-Type": "application/json",`,
    `    "Accept": "application/json",`,
    `}`,
  ];
  if (hasBody(ctx.endpoint.method) && ctx.body) {
    lines.push(``);
    lines.push(`payload = ${indent(ctx.body, 0).trimStart()}`);
    lines.push(``);
    lines.push(
      `response = requests.${ctx.endpoint.method.toLowerCase()}(url, headers=headers, json=payload)`,
    );
  } else {
    lines.push(``);
    lines.push(
      `response = requests.${ctx.endpoint.method.toLowerCase()}(url, headers=headers)`,
    );
  }
  lines.push(`response.raise_for_status()`);
  lines.push(`print(response.json())`);
  return lines.join("\n");
};

const buildGo = (ctx: CodeContext) => {
  const withBody = hasBody(ctx.endpoint.method) && ctx.body;
  const imports = [
    `\t"fmt"`,
    `\t"io"`,
    `\t"net/http"`,
    ...(withBody ? [`\t"strings"`] : []),
  ].join("\n");

  const bodyDecl = withBody
    ? `\tpayload := strings.NewReader(\`${ctx.body}\`)\n\n\t`
    : `\t`;
  const reqArg = withBody ? "payload" : "nil";

  return [
    `package main`,
    ``,
    `import (`,
    imports,
    `)`,
    ``,
    `func main() {`,
    `${bodyDecl}req, _ := http.NewRequest("${ctx.endpoint.method}", "${ctx.url}", ${reqArg})`,
    `\treq.Header.Set("Authorization", "Bearer ${tokenOf(ctx)}")`,
    `\treq.Header.Set("Content-Type", "application/json")`,
    `\treq.Header.Set("Accept", "application/json")`,
    ``,
    `\tresp, err := http.DefaultClient.Do(req)`,
    `\tif err != nil {`,
    `\t\tpanic(err)`,
    `\t}`,
    `\tdefer resp.Body.Close()`,
    ``,
    `\tbody, _ := io.ReadAll(resp.Body)`,
    `\tfmt.Println(string(body))`,
    `}`,
  ].join("\n");
};

const BUILDERS: Record<CodeLanguage, (ctx: CodeContext) => string> = {
  curl: buildCurl,
  node: buildNode,
  python: buildPython,
  go: buildGo,
};

export const generateCodeSample = (
  language: CodeLanguage,
  ctx: CodeContext,
): string => BUILDERS[language](ctx);
